/**
 * Customer account session (Design Specification Section 12; backend/customers/router.py). Separate from the
 * admin token (lib/api.js's ADMIN_TOKEN_KEY / a different signing secret server-side, blueprint Section 6) —
 * this file never reads or writes the admin key, and the admin api instance never carries a customer token.
 *
 * Access tokens last 15 minutes and refresh silently (Section 12.5); nothing here shows a timeout warning.
 * Token storage is a security decision (Statham-CISO); localStorage is used here, consistent with the admin
 * token's existing pattern in this codebase, pending any different ruling.
 */
import axios from "axios";
import { API } from "@/lib/api";

export const CUSTOMER_TOKEN_KEY = "rc_customer_tokens";

export function getTokens() {
  try {
    const raw = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setTokens(tokens) {
  try {
    if (tokens) localStorage.setItem(CUSTOMER_TOKEN_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    // storage unavailable (private mode, etc.) — session just will not persist across reloads
  }
}

export function isSignedIn() {
  return !!(getTokens() && getTokens().access_token);
}

export const customerApi = axios.create({ baseURL: API });

customerApi.interceptors.request.use((config) => {
  const tokens = getTokens();
  if (tokens && tokens.access_token) config.headers.Authorization = `Bearer ${tokens.access_token}`;
  return config;
});

let refreshing = null;

async function doRefresh() {
  const tokens = getTokens();
  if (!tokens || !tokens.refresh_token) throw new Error("no refresh token");
  const res = await axios.post(`${API}/customers/refresh`, { refresh_token: tokens.refresh_token });
  setTokens(res.data);
  return res.data;
}

// One silent refresh-and-retry per request on a 401. If refresh also fails, tokens are cleared and the
// caller's page is responsible for redirecting to sign-in with "Your session ended. Sign in again." (12.5).
customerApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response && error.response.status === 401 && !original._retried && getTokens()) {
      original._retried = true;
      try {
        refreshing = refreshing || doRefresh();
        const tokens = await refreshing;
        refreshing = null;
        original.headers.Authorization = `Bearer ${tokens.access_token}`;
        return customerApi(original);
      } catch {
        refreshing = null;
        setTokens(null);
      }
    }
    return Promise.reject(error);
  }
);

export async function registerCustomer(email, password) {
  const res = await axios.post(`${API}/customers/register`, { email, password });
  return res.data;
}

export async function verifyEmail(token) {
  const res = await axios.post(`${API}/customers/verify-email`, { token });
  return res.data;
}

export async function loginCustomer(email, password) {
  const res = await axios.post(`${API}/customers/login`, { email, password });
  setTokens(res.data);
  return res.data;
}

export async function logoutCustomer() {
  const tokens = getTokens();
  setTokens(null);
  if (tokens && tokens.refresh_token) {
    try {
      await axios.post(`${API}/customers/logout`, { refresh_token: tokens.refresh_token });
    } catch {
      // best-effort: the local session is already cleared either way
    }
  }
}

export async function fetchMe() {
  const res = await customerApi.get("/customers/me");
  return res.data;
}
